import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiUrl } from '../../shared/models/apiurl.models';
import { SystemApp } from '../../constans/SystemConfigurations';

@Component({
    selector: 'app-phone-verify',
    templateUrl: './phone-verify.component.html',
    styleUrls: ['./phone-verify.component.css'],
    standalone: false
})
export class PhoneVerifyComponent implements OnChanges {
  @Input() phone: string = '';
  @Input() email: string = '';
  @Input() contactPerson: string = '';
  @Output() phoneChanged    = new EventEmitter<string>();
  @Output() editModeChange  = new EventEmitter<boolean>();

  // ── edit state ─────────────────────────────────────────────────
  editMode  = false;
  newPhone  = '';
  phoneError = '';

  // ── OTP modal state ────────────────────────────────────────────
  showOtpModal = false;
  otpCode      = '';
  otpError     = '';
  otpSent      = false;   // true once first send succeeded

  // ── loading ────────────────────────────────────────────────────
  sending   = false;
  verifying = false;

  private readonly endpoint = 'clientAjax/adv/phone/validation/';

  constructor(private http: HttpClient, private apiKey: ApiUrl) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['phone'] && !this.editMode) {
      this.newPhone = this.phone;
    }
  }

  // ── helpers ────────────────────────────────────────────────────
  private get headers(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type':  'application/json',
      'Authorization': this.apiKey.getJWTToken()
    });
  }

  private cleanPhone(p: string): string {
    return p.replace(/[\s\-]/g, '');
  }

  isValidIsraeliPhone(p: string): boolean {
    // Israeli mobile: starts with 05, exactly 10 digits
    return /^05\d{8}$/.test(this.cleanPhone(p));
  }

  // ── edit flow ──────────────────────────────────────────────────
  startEdit(): void {
    this.newPhone   = this.phone;
    this.phoneError = '';
    this.editMode   = true;
    this.editModeChange.emit(true);
  }

  cancelEdit(): void {
    this.editMode   = false;
    this.newPhone   = this.phone;
    this.phoneError = '';
    this.closeModal();
    this.editModeChange.emit(false);
  }

  onPhoneInput(): void {
    const cleaned = this.cleanPhone(this.newPhone);
    this.phoneError = cleaned && !this.isValidIsraeliPhone(cleaned)
      ? 'מספר לא תקין — דוגמה: 0501234567'
      : '';
  }

  // ── Step 1: send OTP ──────────────────────────────────────────
  requestOtp(): void {
    const cleaned = this.cleanPhone(this.newPhone);
    if (!this.isValidIsraeliPhone(cleaned)) {
      this.phoneError = 'מספר לא תקין — דוגמה: 0501234567';
      return;
    }

    this.sending    = true;
    this.phoneError = '';

    this.http.post(
      `${SystemApp.base_url_api}/${this.endpoint}`,
      {
        // No consent flags here: changing the account phone must not tick
        // the terms / marketing consent on the user's behalf.
        contactPerson: this.contactPerson || 'user',
        email:         this.email,
        phoneNumber:   cleaned
      },
      { headers: this.headers }
    ).subscribe({
      next: () => {
        this.sending     = false;
        this.otpSent     = true;
        this.otpCode     = '';
        this.otpError    = '';
        this.showOtpModal = true;
      },
      error: (err) => {
        this.sending    = false;
        this.phoneError = err?.error?.message || 'שגיאה בשליחת הקוד, נסה שנית';
      }
    });
  }

  // ── Step 2: verify OTP ────────────────────────────────────────
  verifyOtp(): void {
    if (this.otpCode.trim().length < 4) {
      this.otpError = 'אנא הזן את הקוד שנשלח אליך';
      return;
    }

    this.verifying = true;
    this.otpError  = '';
    const cleaned  = this.cleanPhone(this.newPhone);

    this.http.post(
      `${SystemApp.base_url_api}/${this.endpoint}`,
      {
        contactPerson:  this.contactPerson || 'user',
        email:          this.email,
        phoneNumber:    cleaned,
        codeValidation: this.otpCode.trim()
      },
      { headers: this.headers }
    ).subscribe({
      next: (res: any) => {
        this.verifying    = false;
        // A wrong code answers 203 (a 2xx) - only status "success" verifies.
        if (res?.status !== 'success') {
          this.otpError = res?.message || 'קוד שגוי, אנא נסה שנית';
          return;
        }
        this.showOtpModal = false;
        this.editMode     = false;
        this.phoneChanged.emit(cleaned);
        this.editModeChange.emit(false);
      },
      error: (err) => {
        this.verifying = false;
        this.otpError  = err?.error?.message || 'קוד שגוי, אנא נסה שנית';
        if (err?.error?.statusCode === 'PHONE_LINKED_OTHER_ACCOUNT') {
          this.showOtpModal = false;
          this.phoneError = err.error.message;
        }
      }
    });
  }

  // ── modal helpers ─────────────────────────────────────────────
  closeModal(): void {
    this.showOtpModal = false;
    this.otpCode      = '';
    this.otpError     = '';
  }

  resendOtp(): void {
    this.closeModal();
    this.requestOtp();
  }
}
